import React, { useCallback, useEffect } from 'react';
import { Button } from 'antd';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Column, ColumnGroup } from '@cards/Card';
import DirectChildOrganisationUserProfilePictureCard from '@cards/DirectChildOrganisationUsers/ProfilePicture';
import { usePageActions } from '@hooks';
import {
  loadDirectChildOrganisationUserDetails,
  resetDirectChildOrganisationUserEdit,
} from '@redux/directChildOrganisationAdministration/actions';
import {
  selectDirectChildOrganisationUserDetails,
  selectDirectChildOrganisationUserEdit,
} from '@redux/directChildOrganisationAdministration/selectors';
import submitDirectChildOrganisationUserEdit from '@redux/directChildOrganisationAdministration/submitEdit';
import { setCurrentScreen, screens } from '../../../analytics';
import { matchType } from '../../../propTypes';
import DirectChildOrganisationUserEditWizard from './EditWizard';

const EditDirectChildOrganisationUser = ({ match }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const history = useHistory();
  const { t } = useTranslation();
  const { organisationSlug } = match.params;
  const userId = Number(match.params.userId);
  const details = useSelector(state =>
    selectDirectChildOrganisationUserDetails(state, organisationSlug, userId),
  );
  const edit = useSelector(state =>
    selectDirectChildOrganisationUserEdit(state, organisationSlug, userId),
  );
  const title = t('directChildOrganisationAdministration:routes.editUser');
  const fullName = details.user ? `${details.user.firstName} ${details.user.lastName}`.trim() : '';
  const { hasPageActionsProvider, setTitle } = usePageActions({
    title: fullName || title,
    showClose: true,
  });

  useEffect(() => {
    setTitle(fullName || title);
  }, [fullName, setTitle, title]);

  useEffect(() => {
    dispatch(resetDirectChildOrganisationUserEdit(organisationSlug, userId));
    dispatch(loadDirectChildOrganisationUserDetails(organisationSlug, userId));

    return () => {
      dispatch(resetDirectChildOrganisationUserEdit(organisationSlug, userId));
    };
  }, [dispatch, organisationSlug, userId]);

  useEffect(() => {
    setCurrentScreen(screens.ADMINISTRATION_DIRECT_CHILD_ORGANISATION_USER_EDIT);
  }, []);

  useEffect(() => {
    if (edit.status === 'success') {
      history.replace(`/administration/child-organisation-users/${organisationSlug}/${userId}`);
    }
  }, [edit.status, history, organisationSlug, userId]);

  const onSubmit = useCallback(
    values => {
      if (!details.user) return;
      submitDirectChildOrganisationUserEdit({
        currentUser: details.user,
        values,
        organisationSlug,
        userId,
        dispatch,
        store,
        t,
      });
    },
    [details.user, dispatch, organisationSlug, store, t, userId],
  );

  if (details.status === 'idle' || details.status === 'loading') {
    return (
      <section>
        {!hasPageActionsProvider ? <h1>{title}</h1> : null}
        <p role="status">{t('directChildOrganisationAdministration:users.edit.loading')}</p>
      </section>
    );
  }

  if (details.status === 'failure') {
    const errorKeyByStatus = {
      403: 'forbidden',
      404: 'notFound',
      502: 'upstream',
    };
    const errorKey = errorKeyByStatus[details.errorStatus] || 'loadFailure';

    return (
      <section role="alert">
        {!hasPageActionsProvider ? <h1>{title}</h1> : null}
        <p>{t(`directChildOrganisationAdministration:users.edit.${errorKey}`)}</p>
        <Button
          onClick={() => dispatch(loadDirectChildOrganisationUserDetails(organisationSlug, userId))}
        >
          {t('directChildOrganisationAdministration:users.edit.retry')}
        </Button>
      </section>
    );
  }

  return (
    <>
      {!hasPageActionsProvider ? <h1>{title}</h1> : null}
      <ColumnGroup>
        <Column>
          <DirectChildOrganisationUserEditWizard
            submitting={edit.status === 'loading'}
            user={details.user}
            onSubmit={onSubmit}
          />
        </Column>
        <Column>
          {details.user ? (
            <DirectChildOrganisationUserProfilePictureCard
              organisationSlug={organisationSlug}
              userId={details.user.id}
              displayName={fullName}
              initialImageId={details.user.profilePictureImageId}
              initialImageUrl={details.user.profilePictureUrl}
            />
          ) : null}
        </Column>
      </ColumnGroup>
    </>
  );
};

EditDirectChildOrganisationUser.propTypes = {
  match: matchType.isRequired,
};

export default EditDirectChildOrganisationUser;
